import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";

// Ensure these point to your actual component paths
import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";
import {
  RateCalculatorSection, ReviewPostModal,
  useRateCatalog, useRouteDistance, useEstimate, effectiveEscorts, buildEstimateInputs,
} from "./PostLoadRates";

// ── COLOR THEME (CSS-variable driven) ─────────
const T = {
  bg:"var(--pl-bg)", surface:"var(--pl-surface)", line:"var(--pl-line)", line2:"var(--pl-line2)",
  navy:"var(--pl-navy)",
  teal:"var(--pl-teal)", tealLight:"var(--pl-teal-light)", tealDark:"var(--pl-teal-dark)",
  amber:"var(--pl-amber)", amberLight:"var(--pl-amber-light)",
  slate:"var(--pl-slate)", muted:"var(--pl-muted)", mutedBg:"var(--pl-muted-bg)",
  text:"var(--pl-text)", white:"var(--pl-surface)", onAccent:"#ffffff",
  green:"var(--pl-green)", greenLight:"var(--pl-green-light)",
  red:"var(--pl-red)", redLight:"var(--pl-red-light)",
  shadow:"var(--pl-shadow)",
};

// Shared theme CSS injected once
const THEME_CSS = `
  :root {
    --pl-bg:#f3f4f6; --pl-surface:#ffffff; --pl-line:#e5e7eb; --pl-line2:#f9fafb;
    --pl-navy:#1a2744;
    --pl-teal:#0099a8; --pl-teal-light:#e6f7f9; --pl-teal-dark:#007a87;
    --pl-amber:#e07b00; --pl-amber-light:#fef3e2;
    --pl-slate:#64748b; --pl-muted:#9ca3af; --pl-muted-bg:#f3f4f6;
    --pl-text:#111827;
    --pl-green:#16a34a; --pl-green-light:#f0fdf4;
    --pl-red:#ef4444; --pl-red-light:#fef2f2;
    --pl-shadow: 0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.04);
  }
`;

// ── 100% FREE OPENSTREETMAP LOCATION AUTOCOMPLETE ────────────────────────────
function LocationAutocomplete({ value, onChange, onSelectDetails, placeholder, excludeCity, mode = "city" }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value || "");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);
  const timerRef = React.useRef(null);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/company-login");
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Sync internal query if value is cleared from outside
  React.useEffect(() => {
    if (value !== query) setQuery(value || "");
  }, [value]);

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val); // Keep parent form in sync

    clearTimeout(timerRef.current);
    
    if (val.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&addressdetails=1&limit=5&countrycodes=us,ca`
        );
        const data = await res.json();
        
        const filtered = excludeCity 
          ? data.filter(d => !d.display_name.includes(excludeCity.split(',')[0]))
          : data;
          
        setResults(filtered);
        setOpen(true);
      } catch (err) {
        console.error("Geocoding error:", err);
      } finally {
        setLoading(false);
      }
    }, 600);
  };

  const handleSelect = (item) => {
    const addr = item.address;
    const city = addr.city || addr.town || addr.village || addr.county || addr.municipality || "";
    const state = addr.state || "";
    const zip = addr.postcode || "";
    
    let displayName = "";
    if (mode === "landmark") {
      // For landmarks, keep the business/street name visible
      const placeName = item.name || item.display_name.split(",")[0];
      displayName = (city && state && !placeName.includes(city)) 
          ? `${placeName}, ${city}, ${state}`
          : item.display_name.split(",").slice(0, 3).join(", ");
    } else {
      // For origin/dest, simplify to City, State
      displayName = city && state ? `${city}, ${state}` : item.display_name.split(",").slice(0, 2).join(", ");
    }

    setQuery(displayName);
    onChange(displayName);
    setOpen(false);

    if (onSelectDetails) {
      onSelectDetails({
        displayName,
        city,
        state,
        zip,
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon)
      });
    }
  };

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14 }}>
          {mode === "landmark" ? "🚩" : "📍"}
        </span>
        <input
          type="text"
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder}
          style={{
            width: "100%", background: T.white, border: `1.5px solid ${open ? T.teal : T.line}`,
            borderRadius: 10, padding: "12px 32px 12px 36px", color: T.text, fontSize: 14,
            outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s"
          }}
        />
        {loading && (
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: T.teal }}>
            ⟳
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: T.white, border: `1px solid ${T.line}`, borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)", zIndex: 9999, overflow: "hidden",
        }}>
          {results.map((item, idx) => (
            <div
              key={item.place_id || idx}
              onClick={() => handleSelect(item)}
              style={{
                padding: "10px 14px", cursor: "pointer", display: "flex", flexDirection: "column", gap: 2,
                borderBottom: idx === results.length - 1 ? "none" : `1px solid ${T.line2}`
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.tealLight}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: T.text }}>
                {item.display_name.split(",").slice(0, mode === "landmark" ? 3 : 2).join(", ")}
              </span>
              <span style={{ fontSize: 11, color: T.muted }}>
                {item.display_name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const LOAD_TYPES = [
  "Wind Turbine Blade","Industrial Generator","Bridge Beam","Mobile Home",
  "Drilling Rig Component","Oversized Excavator","Transformer","Prefab Structure",
  "Large HVAC Unit","Crane Component","Oil Rig Equipment","Custom/Other",
];

const PAY_PRESETS = ["$500 - $1,000", "$1,000 - $2,500", "$2,500 - $5,000", "Custom Amount"];

// ── Client-side mirror of the backend's Auto-Escort Requirement Engine ──
// Same thresholds as loads/escort_engine.py, so the preview panel updates
// live as the company types — the real, authoritative computation still
// happens server-side on submit (see LoadCreateSerializer).
function parseDimToInches(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase().replace(/"/g, "").replace(/in/g, "").trim();
  const m = s.match(/^(\d+(?:\.\d+)?)\s*'?\s*-?\s*(\d+(?:\.\d+)?)?\s*(?:ft)?$/);
  if (!m) { const n = parseFloat(s); return isNaN(n) ? null : n * 12; }
  const feet = parseFloat(m[1] || 0), inches = parseFloat(m[2] || 0);
  return feet * 12 + inches;
}
function parseWeightLbs(raw) {
  if (!raw) return null;
  const n = parseFloat(String(raw).replace(/[^\d.]/g, ""));
  return isNaN(n) ? null : n;
}
function computeEscortFlagsClient(width, height, weight) {
  const w = parseDimToInches(width), h = parseDimToInches(height), lbs = parseWeightLbs(weight);
  const flags = { needsFront:false, needsChase:false, needsPole:false, needsSteersman:false, chpRequired:false, restrictedHours:false, warnings:[] };
  if (w !== null) {
    if (w >= 168) { flags.needsFront = flags.needsChase = true; flags.chpRequired = true; flags.restrictedHours = true;
      flags.warnings.push({ level:"high", text:"Width ≥ 14' — police/CHP escort very likely required." }); }
    else if (w >= 144) { flags.needsFront = flags.needsChase = true;
      flags.warnings.push({ level:"medium", text:"Width ≥ 12' — front AND chase escorts typically required." }); }
    else if (w >= 102) { flags.needsChase = true;
      flags.warnings.push({ level:"low", text:"Width ≥ 8'6\" — an escort is typically required." }); }
  }
  if (h !== null) {
    if (h >= 174) { flags.needsPole = true; flags.restrictedHours = true;
      flags.warnings.push({ level:"high", text:"Height ≥ 14'6\" — high-pole escort typically required, daylight-only travel." }); }
    else if (h >= 162) { flags.warnings.push({ level:"medium", text:"Height ≥ 13'6\" — over routine legal max in most states; pre-clear the route." }); }
  }
  if (lbs !== null && lbs >= 120000) { flags.needsSteersman = true;
    flags.warnings.push({ level:"medium", text:"Weight ≥ 120,000 lbs — rear-steer axle & steersman commonly required." }); }
  if (flags.restrictedHours) flags.warnings.push({ level:"low", text:"Likely restricted from night/weekend/holiday travel — verify against the permit." });
  return flags;
}

const PAY_TYPES = ["Fixed Rate", "Negotiable", "Per Mile", "Hourly"];

const WIDTH_OPTIONS = [
  "8' 0\"", "8' 6\"", "9' 0\"", "10' 0\"", "11' 0\"",
  "12' 0\"", "12' 6\"", "13' 0\"", "14' 0\"", "14' 6\"",
  "15' 0\"", "16' 0\"", "17' 0\"", "18' 0\"", "20' 0\"", "Custom",
];

const HEIGHT_OPTIONS = [
  "13' 0\"", "13' 6\"", "14' 0\"", "14' 6\"", "15' 0\"",
  "15' 6\"", "16' 0\"", "16' 6\"", "17' 0\"", "18' 0\"", "Custom",
];

// ── REUSABLE UI COMPONENTS ───────────────────────────────────────────
function RouteMap({ origin, destination, originCoords, destCoords, meetingCoords, meetingName, distanceMiles }) {
  const fromCoords = originCoords || [39.8283, -98.5795];
  const toCoords   = destCoords || [39.8283, -98.5795];

  const midLat  = (fromCoords[0] + toCoords[0]) / 2;
  const midLng  = (fromCoords[1] + toCoords[1]) / 2;
  const dLat    = toCoords[0] - fromCoords[0];
  const dLng    = toCoords[1] - fromCoords[1];
  const distMi  = distanceMiles ? Math.round(distanceMiles) : (() => { const r = (x) => (x * Math.PI) / 180; const h = Math.sin(r(dLat) / 2) ** 2 + Math.cos(r(fromCoords[0])) * Math.cos(r(toCoords[0])) * Math.sin(r(dLng) / 2) ** 2; return Math.round(2 * 3958.8 * Math.asin(Math.sqrt(h)) * 1.18); })();
  const zoom = distMi < 80 ? 8 : distMi < 200 ? 7 : distMi < 400 ? 6 : 5;

  const mapHtml = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>*{margin:0;padding:0;box-sizing:border-box;}html,body,#map{width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([${midLat},${midLng}],${zoom});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(map);

var fromPt = [${fromCoords[0]},${fromCoords[1]}];
var toPt   = [${toCoords[0]},${toCoords[1]}];
var dLat = toPt[0] - fromPt[0];
var dLng = toPt[1] - fromPt[1];
var pts = [];
var steps = 20;
for(var i=0;i<=steps;i++){
  var t = i/steps;
  var cx = (fromPt[1]+toPt[1])/2 + dLat*0.15;
  var cy = (fromPt[0]+toPt[0])/2 - dLng*0.15;
  var lat = (1-t)*(1-t)*fromPt[0] + 2*(1-t)*t*cy + t*t*toPt[0];
  var lng = (1-t)*(1-t)*fromPt[1] + 2*(1-t)*t*cx + t*t*toPt[1];
  pts.push([lat,lng]);
}

L.polyline(pts,{color:'#0099a8',weight:4,opacity:0.85,dashArray:null}).addTo(map);
L.polyline(pts,{color:'#005f6b',weight:6,opacity:0.2}).addTo(map);

var originIcon = L.divIcon({html:'<div style="background:#e07b00;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',iconAnchor:[7,7],className:''});
var destIcon   = L.divIcon({html:'<div style="background:#0099a8;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>',iconAnchor:[9,9],className:''});

L.marker(fromPt,{icon:originIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${origin.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});
L.marker(toPt,  {icon:destIcon  }).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${destination.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});

${meetingCoords && meetingCoords[0] ? `
var meetingPt = [${meetingCoords[0]},${meetingCoords[1]}];
var meetIcon = L.divIcon({html:'<div style="background:#7c3aed;width:14px;height:14px;border:3px solid white;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);"></div>',iconAnchor:[7,7],className:''});
var meetName = "${meetingName ? meetingName.split(",")[0].replace(/"/g, '\\"').replace(/'/g, "\\'") : 'Meeting Point'}";
L.marker(meetingPt,{icon:meetIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">🚩 ' + meetName + '</b>',{permanent:true,direction:'top', offset:[0,-10]});
var bounds = L.latLngBounds([fromPt, toPt, meetingPt]);
map.fitBounds(bounds, {padding: [30, 30]});
` : ''}
</script>
</body></html>`;

  return (
    <div style={{ marginTop:16, borderRadius:12, overflow:"hidden", border:`1px solid ${T.line}`, boxShadow:"0 2px 8px #0000000f" }}>
      <div style={{ background:T.tealLight, padding:"10px 16px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:`1px solid ${T.teal}20` }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span className="truncate max-w-[100px] sm:max-w-none" style={{ color:T.amber, fontWeight:800, fontSize:13 }}>📍 {origin}</span>
          <span style={{ color:T.muted, fontSize:14 }}>→</span>
          <span className="truncate max-w-[100px] sm:max-w-none" style={{ color:T.teal, fontWeight:800, fontSize:13 }}>🏁 {destination}</span>
        </div>
        <span style={{ color:T.teal, fontSize:12, fontWeight:700, flexShrink:0 }}>~{distMi} mi</span>
      </div>
      <iframe srcDoc={mapHtml} style={{ width:"100%", height:260, border:"none", display:"block" }} title="Route Map" />
    </div>
  );
}

function Label({ children, required }) {
  return (
    <div style={{ color:T.slate, fontSize:12, fontWeight:600, letterSpacing:"1px", textTransform:"uppercase", marginBottom:6 }}>
      {children}{required && <span style={{ color:T.red, marginLeft:3 }}>*</span>}
    </div>
  );
}

function Field({ children, style, className }) {
  return <div className={className} style={{ marginBottom:16, ...style }}>{children}</div>;
}

function Input({ value, onChange, placeholder, type="text", prefix, suffix }) {
  return (
    <div style={{ position:"relative" }}>
      {prefix && <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", color:T.muted, fontSize:14 }}>{prefix}</span>}
      <input type={type} value={value} onChange={e=>onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width:"100%", background:T.white, border:`1.5px solid ${T.line}`,
          borderRadius:10, padding:prefix?"12px 12px 12px 32px":suffix?"12px 52px 12px 12px":"12px",
          color:T.text, fontSize:14, outline:"none", boxSizing:"border-box", fontFamily:"inherit", transition:"border-color 0.2s" }}
        onFocus={e=>e.target.style.borderColor=T.teal}
        onBlur={e=>e.target.style.borderColor=T.line}/>
      {suffix && <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:T.muted, fontSize:13 }}>{suffix}</span>}
    </div>
  );
}

function Select({ value, onChange, options, placeholder }) {
  return (
    <div style={{ position:"relative" }}>
      <select value={value} onChange={e=>onChange(e.target.value)}
        style={{ width:"100%", background:T.white, border:`1.5px solid ${T.line}`,
          borderRadius:10, padding:"12px 36px 12px 12px", color:value?T.text:T.muted,
          fontSize:14, outline:"none", cursor:"pointer", fontFamily:"inherit", appearance:"none",
          backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6,9 12,15 18,9'/%3E%3C/svg%3E")`,
          backgroundRepeat:"no-repeat", backgroundPosition:"right 12px center", transition:"border-color 0.2s" }}
        onFocus={e=>e.target.style.borderColor=T.teal}
        onBlur={e=>e.target.style.borderColor=T.line}>
        <option value="">{placeholder}</option>
        {options.map(o=><option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Dropdown({ value, onChange, options, placeholder }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div ref={wrapRef} style={{ position:"relative" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
          background:T.white, border:`1.5px solid ${open ? T.teal : T.line}`, borderRadius:10,
          padding:"12px 12px", color: value ? T.text : T.muted, fontSize:14, fontFamily:"inherit",
          cursor:"pointer", transition:"border-color 0.2s",
        }}
      >
        <span className="truncate text-left">{value || placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"
          style={{ flexShrink:0, marginLeft:8, transform: open ? "rotate(180deg)" : "none", transition:"transform 0.15s" }}>
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:30,
          background:T.surface, border:`1px solid ${T.line}`, borderRadius:12,
          boxShadow:"0 12px 32px rgba(0,0,0,0.18)", overflow:"hidden", padding:4,
        }}>
          {options.map(o => (
            <div
              key={o}
              onClick={() => { onChange(o); setOpen(false); }}
              style={{
                padding:"10px 12px", borderRadius:8, cursor:"pointer", fontSize:13.5,
                display:"flex", alignItems:"center", justifyContent:"space-between",
                color: value === o ? T.teal : T.text, fontWeight: value === o ? 700 : 500,
                background: value === o ? T.tealLight : "transparent",
              }}
              onMouseEnter={e => { if (value !== o) e.currentTarget.style.background = T.line2; }}
              onMouseLeave={e => { if (value !== o) e.currentTarget.style.background = "transparent"; }}
            >
              {o}
              {value === o && <span style={{ fontSize:12 }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentedControl({ value, onChange, options }) {
  return (
    <div className="flex overflow-x-auto" style={{ background:T.line2, border:`1px solid ${T.line}`, borderRadius:10, padding:3, gap:2 }}>
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className="flex-1 flex-shrink-0"
          style={{
            padding:"9px 10px", borderRadius:8, border:"none", fontSize:12.5, fontWeight:700,
            whiteSpace:"nowrap", cursor:"pointer", transition:"all 0.15s",
            background: value === opt ? T.navy : "transparent",
            color: value === opt ? T.onAccent : T.slate,
          }}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

function Checkbox({ label, checked, onChange, sub }) {
  return (
    <div onClick={()=>onChange(!checked)}
      style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"13px 16px",
        borderRadius:11, border:`1.5px solid ${checked?T.teal:T.line}`,
        background:checked?T.tealLight:T.white, cursor:"pointer", transition:"all 0.15s" }}>
      <div style={{ width:20, height:20, borderRadius:5, flexShrink:0, marginTop: 2,
        border:`2px solid ${checked?T.teal:T.muted}`, background:checked?T.teal:"transparent",
        display:"flex", alignItems:"center", justifyContent:"center" }}>
        {checked && <span style={{ color:T.onAccent, fontSize:13, fontWeight:800 }}>✓</span>}
      </div>
      <div className="min-w-0">
        <div style={{ color:checked?T.teal:T.text, fontWeight:600, fontSize:14 }} className="truncate">{label}</div>
        {sub && <div style={{ color:T.muted, fontSize:11, marginTop:1, lineHeight:1.3 }}>{sub}</div>}
      </div>
    </div>
  );
}

function SectionCard({ icon, title, eyebrow, children }) {
  return (
    <div className="rounded-2xl p-4 sm:p-7 mb-4 sm:mb-5" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
      <div className="flex items-center gap-3 mb-5 sm:mb-6 pb-4 sm:pb-5" style={{ borderBottom:`1px solid ${T.line2}` }}>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background:T.tealLight, border:`1px solid ${T.teal}25` }}>
          <span className="text-[18px] leading-none">{icon}</span>
        </div>
        <div className="min-w-0">
          {eyebrow && <div className="text-[10px] font-bold uppercase tracking-[1.5px] mb-0.5" style={{ color:T.teal }}>{eyebrow}</div>}
          <div className="font-extrabold text-[15px] sm:text-[17px] truncate" style={{ color:T.text, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{title}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

// ── RIGHT SIDEBAR PREVIEW ────────────────────────────────────────────
function LoadPreview({ form, featured, payDisplay }) {
  const hasRoute = form.origin && form.destination;
  const typeColor = {
    "Wind Turbine Blade":T.teal,"Bridge Beam":"#ea580c","Mobile Home":T.green,
    "Industrial Generator":"#7c3aed","Drilling Rig Component":T.amber,
  }[form.loadType] || T.slate;

  return (
    <div className="rounded-[14px] p-4 sm:p-5" style={{ background:T.surface, border:`2px solid ${T.teal}`, boxShadow:T.shadow }}>
      <div style={{ color:T.teal, fontSize:10, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase", marginBottom:10 }}>LOAD BOARD PREVIEW</div>
      {!hasRoute ? (
        <div style={{ textAlign:"center", padding:"24px 0", color:T.muted, fontSize:13 }}>Fill in route details to preview your listing</div>
      ) : (
        <>
          <div className="flex justify-between items-start mb-3 gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex gap-1.5 flex-wrap mb-2">
                {featured && <span style={{ background:`${T.amber}18`, color:T.amber, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:99, border:`1px solid ${T.amber}30`, whiteSpace:"nowrap" }}>⚡ Featured</span>}
                <span style={{ background:T.tealLight, color:T.teal, fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:99, border:`1px solid ${T.teal}30`, whiteSpace:"nowrap" }}>✓ PPCA</span>
              </div>
              <div className="font-extrabold text-[14px] sm:text-[15px] mb-1 leading-snug break-words" style={{ color:T.text }}>
                {form.origin}{form.originZip && <span style={{ fontSize:11, fontWeight:600, color:T.muted }}> {form.originZip}</span>}
                {" "}<br className="sm:hidden" /><span className="hidden sm:inline">→</span><span className="sm:hidden">↓</span>{" "}
                {form.destination}{form.destinationZip && <span style={{ fontSize:11, fontWeight:600, color:T.muted }}> {form.destinationZip}</span>}
              </div>
              <div style={{ color:T.slate, fontSize:12 }}>{form.date || "TBD"} · Pickup</div>
            </div>
            <div className="rounded-[10px] p-2 sm:px-3 sm:py-2 text-center flex-shrink-0" style={{ background:T.greenLight, border:`1px solid ${T.green}30` }}>
              <div style={{ color:T.muted, fontSize:9, fontWeight:600, textTransform:"uppercase", letterSpacing:"1px" }}>Est. Pay</div>
              <div style={{ color:T.green, fontWeight:800, fontSize:16 }} className="sm:text-[18px]">
                {payDisplay || "TBD"}
              </div>
            </div>
          </div>
          {form.loadType && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg mb-3" style={{ background:`${typeColor}12`, border:`1px solid ${typeColor}25` }}>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background:typeColor }}/>
              <span className="text-[11px] font-bold" style={{ color:typeColor }}>{form.loadType}</span>
            </div>
          )}
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            {[
              { label:"Width", value:form.width||"—" },
              { label:"Height", value:form.height||"—" },
              { label:"Weight", value:form.weight?`${form.weight} lbs`:"—" },
              { label:"Escorts", value:[form.frontEscort, form.chaseEscort, form.heightPole, form.steersman].filter(Boolean).length||"—" },
            ].map(s=>(
              <div key={s.label} className="rounded-lg p-2 text-center" style={{ background:T.line2 }}>
                <div style={{ color:T.muted, fontSize:9, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.5px" }}>{s.label}</div>
                <div className="font-bold text-[11px] mt-1" style={{ color:T.text }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 space-y-1" style={{ borderTop:`1px solid ${T.line2}` }}>
            {computeEscortFlagsClient(form.width, form.height, form.weight).warnings.slice(0,2).map((w,i) => (
              <div key={i} className="flex items-start gap-1.5 text-[10.5px]" style={{ color:T.slate }}>
                <span className="flex-shrink-0">⚠️</span><span>{w.text}</span>
              </div>
            ))}
            <div className="flex items-start gap-1.5 text-[10.5px]" style={{ color:T.muted }}>
              <span className="flex-shrink-0">⚠️</span><span>Low bridges &amp; construction zones — verify manually along this route</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── SUCCESS SCREEN ───────────────────────────────────────────────────
function SuccessScreen({ form, onReset, refCode }) {
  return (
    <div className="flex-1 flex items-center justify-center p-5 font-sans" style={{ background: T.bg }}>
      <div style={{ maxWidth:480, width:"100%", textAlign:"center" }}>
        <div style={{ fontSize:60, marginBottom:16 }}>🚛</div>
        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:800, color:T.green, marginBottom:8 }}>Load Posted!</div>
        {refCode && <div style={{ color:T.slate, fontSize:13, fontWeight:700, marginBottom:12 }}>Reference <span style={{ color:T.teal }}>{refCode}</span> — drivers see the same rates and terms you confirmed.</div>}
        <div style={{ color:T.slate, fontSize:14, lineHeight:1.7, marginBottom:24 }}>
          Your load is now live on the PilotLink load board. Certified drivers in your area will be notified immediately.
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={onReset} className="flex-1" style={{ padding:"13px", borderRadius:12, border:`1.5px solid ${T.line}`, background:T.white, color:T.slate, fontWeight:700, fontSize:14, cursor:"pointer" }}>
            Post Another Load
          </button>
          <Link to="/my-loads" className="flex-1" style={{ padding:"13px", borderRadius:12, border:"none", background:T.teal, color:T.onAccent, fontWeight:800, fontSize:14, cursor:"pointer", textDecoration:"none", display:"inline-block" }}>
            View Dashboard →
          </Link>
        </div>
      </div>
    </div>
  );
}

// ── ZIP CODE AUTOCOMPLETE (shipping-site style dropdown) ──────────────────────
function ZipLookupInput({ value, onChange, onCityFound, placeholder = "e.g. 77001", linkedCity }) {
  const [status, setStatus] = React.useState("idle");
  const [suggestions, setSuggestions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(null);
  const timerRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  React.useEffect(() => {
    clearTimeout(timerRef.current);
    setConfirmed(null);
    if (value.length < 3) {
      setStatus("idle"); setSuggestions([]); setOpen(false);
      return;
    }
    setStatus("loading");
    timerRef.current = setTimeout(async () => {
      try {
        if (value.length === 5) {
          const res = await fetch(`https://api.zippopotam.us/us/${value}`);
          if (!res.ok) throw new Error("not found");
          const data = await res.json();
          const place = data.places?.[0];
          if (!place) throw new Error("no places");
          const geoRes = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${value}&country=us&format=json&limit=1`);
          const geoData = await geoRes.json();
          const lat = geoData[0] ? parseFloat(geoData[0].lat) : null;
          const lng = geoData[0] ? parseFloat(geoData[0].lon) : null;
          const city = place["place name"], state = place["state abbreviation"];
          setSuggestions([{ city, state, zip: value, lat, lng }]);
          setStatus("found");
          setOpen(true);
        } else {
          const res = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${value}&country=us&format=json&addressdetails=1&limit=6`);
          const data = await res.json();
          const opts = data
            .map(d => ({
              city: d.address?.city || d.address?.town || d.address?.village || d.address?.county || "",
              state: d.address?.state_code?.toUpperCase() || d.address?.state || "",
              zip: d.address?.postcode || value,
              lat: parseFloat(d.lat), lng: parseFloat(d.lon),
            }))
            .filter(o => o.city && o.zip.startsWith(value));
          const seen = new Set();
          const deduped = opts.filter(o => (seen.has(o.zip) ? false : (seen.add(o.zip), true)));
          setSuggestions(deduped);
          setStatus(deduped.length ? "found" : "error");
          setOpen(deduped.length > 0);
        }
      } catch {
        setStatus("error"); setSuggestions([]); setOpen(false);
      }
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [value]);

  const handleInput = (v) => onChange(v.replace(/\D/g, "").slice(0, 5));

  const handleSelect = (item) => {
    onChange(item.zip);
    setConfirmed(item);
    setOpen(false);
    onCityFound(item);
  };

  const borderColor = status === "found" ? T.green : status === "error" ? T.red : status === "loading" ? T.teal : T.line;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={e => handleInput(e.target.value)}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          placeholder={placeholder}
          maxLength={5}
          style={{
            width: "100%", background: T.white,
            border: `1.5px solid ${borderColor}`,
            borderRadius: 10, padding: "12px 36px 12px 12px",
            color: T.text, fontSize: 14, outline: "none",
            boxSizing: "border-box", fontFamily: "inherit",
            transition: "border-color 0.2s",
            letterSpacing: value ? "2px" : "normal",
            fontWeight: value ? 700 : 400,
          }}
        />
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 14, pointerEvents: "none" }}>
          {status === "loading" && <span style={{ color: T.teal }}>⟳</span>}
          {status === "found" && confirmed && <span style={{ color: T.green }}>✓</span>}
          {status === "error"   && <span style={{ color: T.red }}>✗</span>}
        </span>
      </div>

      {open && suggestions.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: 220, right: 0,
          background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.18)", zIndex: 9999, overflow: "hidden",
        }}>
          {suggestions.map((s, idx) => (
            <div
              key={s.zip + idx}
              onClick={() => handleSelect(s)}
              style={{
                padding: "9px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8,
                borderBottom: idx === suggestions.length - 1 ? "none" : `1px solid ${T.line2}`,
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.tealLight}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <span style={{ fontSize: 12 }}>📍</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{s.zip}</span>
              <span style={{ fontSize: 12, color: T.muted }}>{s.city}, {s.state}</span>
            </div>
          ))}
        </div>
      )}

      {confirmed && (
        <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, background: T.greenLight, border: `1.5px solid ${T.green}30` }}>
          <span style={{ fontSize: 12 }}>📍</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: T.green, fontWeight: 800, fontSize: 12 }}>{confirmed.city}, {confirmed.state}</span>
            {linkedCity && linkedCity !== `${confirmed.city}, ${confirmed.state}` && (
              <span style={{ color: T.muted, fontSize: 11, marginLeft: 5 }}>applied</span>
            )}
          </div>
          <span style={{ background: T.green, color: T.onAccent, fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 5, flexShrink: 0, letterSpacing: "0.5px" }}>✓ SET</span>
        </div>
      )}

      {status === "error" && !confirmed && (
        <div style={{ marginTop: 5, fontSize: 11, color: T.red, display: "flex", alignItems: "center", gap: 5 }}>
          <span>✗</span> No matching ZIP found
        </div>
      )}
    </div>
  );
}

// ── MAIN PAGE COMPONENT ──────────────────────────────────────────────
export default function PostLoadPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    origin:"", originZip:"", originLat: null, originLng: null,
    destination:"", destinationZip:"", destinationLat: null, destinationLng: null,
    date:"", time:"",
    meetingPoint:"", meetingCity:"", meetingState:"", meetingZip:"", meetingTime:"", meetingLat: null, meetingLng: null,
    loadType:"", commodity:"",
    width:"", height:"", length:"48'", weight:"80,000",
    escortConfirmed:false, truckerConfirmed:false,
    frontEscort:false, chaseEscort:false, heightPole:false, steersman:false,
    overnight:false, overnightRate:"125", pay:"", customPay:"", payType:"Per Mile",
    mileageRate:"", hourlyRate:"", minDailyPay:"",
    highPoleRate:"", estHours:"", waitHours:"", detentionRate:"", nights:"1",
    deadheadMiles:"", deadheadRate:"", nightOps:false, distanceMiles:"",
    heightPolePay:"", escortAddons:"", fuelSurcharge:"Not Included",
    paymentMethod:"Zelle", paymentTerms:"NET 7",
    companyName:"", dispatcherName:"", dispatchPhone:"", dispatchEmail:"", recordsEmail:"",
    notes:"",
  });
  
  const [featured, setFeatured] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [permitText, setPermitText] = useState("");
  const [permitLoading, setPermitLoading] = useState(false);
  const [permitError, setPermitError] = useState("");

  const escortFlags = React.useMemo(
    () => computeEscortFlagsClient(form.width, form.height, form.weight),
    [form.width, form.height, form.weight]
  );

  const handlePermitExtract = async () => {
    setPermitLoading(true);
    setPermitError("");
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const res = await fetch(`${apiUrl}/api/loads/permit/extract/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text: permitText }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPermitError(data.error || "Couldn't extract this permit. Try again or fill the form manually.");
        return;
      }
      const d = data.data || {};
      setForm(f => ({
        ...f,
        origin: d.origin || f.origin,
        destination: d.destination || f.destination,
        width: d.width || f.width,
        height: d.height || f.height,
        length: d.length || f.length,
        weight: d.weight || f.weight,
        frontEscort: d.escort_requirements?.some(e => /front|lead/i.test(e)) || f.frontEscort,
        chaseEscort: d.escort_requirements?.some(e => /chase|rear/i.test(e)) || f.chaseEscort,
        heightPole: d.escort_requirements?.some(e => /pole/i.test(e)) || f.heightPole,
        notes: [f.notes, d.route_summary, d.curfew && `Curfew: ${d.curfew}`, d.notes]
          .filter(Boolean).join("\n").trim(),
      }));
      setPermitError("✓ Fields updated from the permit — double-check before posting.");
    } catch (err) {
      setPermitError("Network error extracting the permit.");
    } finally {
      setPermitLoading(false);
    }
  };
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(2);
  const [activeNav, setActiveNav] = useState("loads");

  const user = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token"); 

  const upd = useCallback((k, v) => setForm(p => ({ ...p, [k]: v })), []);

  // ── Rate calculator + review step (see PostLoadRates.jsx) ──
  const catalog = useRateCatalog(token);
  const { miles, source: milesSource, loading: milesLoading } =
    useRouteDistance(form.originLat, form.originLng, form.destinationLat, form.destinationLng);
  const effEsc = effectiveEscorts(form, escortFlags);
  const estimate = useEstimate(buildEstimateInputs(form, miles, effEsc), token);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [postedRef, setPostedRef] = useState("");
  useEffect(() => { upd("distanceMiles", miles ? String(miles) : ""); }, [miles, upd]);

  useEffect(() => {
    if (!token) {
      navigate("/company-login", { replace: true });
    }
  }, [token, navigate]);

  if (!token) {
    return null;
  }

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  const total = featured ? 35 : 15;
  const canSubmit = form.origin && form.destination && form.date;

  const handlePostLoad = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const payload = {
        ...form,
        departure_date: form.date, 
        departure_time: form.time, 
        needs_front_escort: form.frontEscort, 
        needs_chase_escort: form.chaseEscort,
        needs_height_pole: form.heightPole,
        needs_steersman: form.steersman,
        featured: featured,
        company_email: form.dispatchEmail || user?.email || "",
        origin_zip:      form.originZip,
        origin_lat:      form.originLat,
        origin_lng:      form.originLng,
        destination_zip: form.destinationZip,
        destination_lat: form.destinationLat,
        destination_lng: form.destinationLng,
        meeting_lat:     form.meetingLat,
        meeting_lng:     form.meetingLng
      };
      
      const response = await fetch(`${apiUrl}/api/loads/create/`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload)
      });
      
      const text = await response.text();
      if (text.trim().startsWith("<")) {
         throw new Error("Server returned an HTML page instead of JSON. Ensure your API URL and Token are correct.");
      }
      
      const data = JSON.parse(text);
      if (response.ok && data.success) {
        setPostedRef(data.data?.id || "");
        setReviewOpen(false);
        setSubmitted(true);
      } else {
        alert("Failed to post load: " + JSON.stringify(data.errors || data.message));
      }
    } catch (error) {
      console.error("Error posting load:", error);
      alert(error.message || "Network error occurred while trying to post the load.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif", color:T.text, background:T.bg }}>
      <style dangerouslySetInnerHTML={{__html: `
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=Inter:wght@400;500;600;700&display=swap');
        ${THEME_CSS}
        select option {background:var(--pl-surface);color:var(--pl-text);}
        input::placeholder, textarea::placeholder {color:var(--pl-muted);}
        ::-webkit-scrollbar {width:10px;height:10px;}
        ::-webkit-scrollbar-thumb {background:var(--pl-line);border-radius:8px;}
      `}} />

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar 
        activeNav={activeNav} setActiveNav={setActiveNav} 
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} 
        initials={initials} companyName={companyName} 
        loads={[]} activePostings={0} assignedLoads={0} totalSpent={0} 
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0" style={{ background:T.bg }}>
        
        <TopBar 
          title="Post a Load" 
          setSidebarOpen={setSidebarOpen} 
          initials={initials} 
          notifCount={notifCount} 
          setNotifCount={setNotifCount} 
        />

        <main className="flex-1 overflow-y-auto">
          {submitted ? (
            <SuccessScreen 
              form={form} refCode={postedRef} 
              onReset={() => {
                setSubmitted(false);
                setForm(f => ({ ...f, destination:"", date:"", loadType:"", pay:"", customPay:"" }));
              }} 
            />
          ) : (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-24 lg:pb-8">
              
              <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl sm:text-3xl">🚛</span>
                  <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, color:T.text }} className="text-[22px] sm:text-3xl truncate">
                    Post an Oversize Load
                  </h1>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start w-full relative">
                
                {/* ── LEFT FORM ── */}
                <div className="flex-1 w-full min-w-0 space-y-4 sm:space-y-6">
                  {/* 1. Route Information */}
                  <SectionCard icon="📍" title="Route Information" eyebrow="Route & Schedule">
                    
                    {/* Origin row */}
                    <div className="flex flex-col sm:flex-row gap-3.5 mb-3.5">
                      <Field className="flex-1 mb-0">
                        <Label required>Origin (City, State)</Label>
                        <LocationAutocomplete 
                          value={form.origin} 
                          onChange={v=>upd("origin",v)} 
                          placeholder="Type any city or address..." 
                          onSelectDetails={(details) => {
                            upd("origin", `${details.city}, ${details.state}`);
                            upd("originZip", details.zip);
                            upd("originLat", details.lat);
                            upd("originLng", details.lng);
                          }}
                        />
                      </Field>
                      <Field className="mb-0" style={{ width:"140px", flexShrink:0 }}>
                        <Label>Origin ZIP</Label>
                        <ZipLookupInput
                          value={form.originZip}
                          onChange={v => upd("originZip", v)}
                          linkedCity={form.origin}
                          placeholder="e.g. 77001"
                          onCityFound={({ city, state, lat, lng }) => {
                            upd("origin", `${city}, ${state}`);
                            if(lat && lng){ upd("originLat", lat); upd("originLng", lng); }
                          }}
                        />
                      </Field>
                    </div>

                    {/* Destination row */}
                    <div className="flex flex-col sm:flex-row gap-3.5 mb-3.5">
                      <Field className="flex-1 mb-0">
                        <Label required>Destination (City, State)</Label>
                        <LocationAutocomplete 
                          value={form.destination} 
                          onChange={v=>upd("destination",v)} 
                          excludeCity={form.origin}
                          placeholder="Type any city or address..." 
                          onSelectDetails={(details) => {
                            upd("destination", `${details.city}, ${details.state}`);
                            upd("destinationZip", details.zip);
                            upd("destinationLat", details.lat);
                            upd("destinationLng", details.lng);
                          }}
                        />
                      </Field>
                      <Field className="mb-0" style={{ width:"140px", flexShrink:0 }}>
                        <Label>Destination ZIP</Label>
                        <ZipLookupInput
                          value={form.destinationZip}
                          onChange={v => upd("destinationZip", v)}
                          linkedCity={form.destination}
                          placeholder="e.g. 30301"
                          onCityFound={({ city, state, lat, lng }) => {
                            upd("destination", `${city}, ${state}`);
                            if(lat && lng){ upd("destinationLat", lat); upd("destinationLng", lng); }
                          }}
                        />
                      </Field>
                    </div>

                    {/* Date + Time row */}
                    <div className="flex flex-col sm:flex-row gap-3.5 mb-3.5">
                      <Field className="flex-1 mb-0">
                        <Label required>Departure Date</Label>
                        <Input type="date" value={form.date} onChange={v=>upd("date",v)} placeholder=""/>
                      </Field>
                      <Field className="flex-1 mb-0">
                        <Label>Departure Time</Label>
                        <Input type="time" value={form.time} onChange={v=>upd("time",v)} placeholder=""/>
                      </Field>
                    </div>

                    {/* Meeting Point Details */}
                    <div className="rounded-xl p-3 sm:p-4 mt-4 sm:mt-5" style={{ background:T.line2, border:`1px solid ${T.line}` }}>
                      <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color:T.tealDark }}>📍 Meeting Point Setup</div>
                      <Field className="mb-3">
                        <Label>Street Address / Landmark</Label>
                        <LocationAutocomplete 
                          mode="landmark"
                          value={form.meetingPoint} 
                          onChange={v=>upd("meetingPoint",v)} 
                          placeholder="Search for truck stop or address..." 
                          onSelectDetails={(details) => {
                            upd("meetingPoint", details.displayName);
                            upd("meetingCity", details.city);
                            upd("meetingState", details.state);
                            upd("meetingZip", details.zip);
                            upd("meetingLat", details.lat);
                            upd("meetingLng", details.lng);
                          }}
                        />
                      </Field>
                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
                        <Field className="sm:col-span-2 mb-0"><Label>City</Label><Input value={form.meetingCity} onChange={v=>upd("meetingCity",v)} placeholder="City" /></Field>
                        <Field className="mb-0"><Label>State</Label><Input value={form.meetingState} onChange={v=>upd("meetingState",v)} placeholder="TX" /></Field>
                        <Field className="mb-0">
                          <Label>ZIP Code</Label>
                          <ZipLookupInput
                            value={form.meetingZip}
                            onChange={v => upd("meetingZip", v)}
                            placeholder="79601"
                            onCityFound={({ city, state, zip }) => {
                              upd("meetingZip",   zip);
                              upd("meetingCity",  city);
                              upd("meetingState", state);
                            }}
                          />
                        </Field>
                      </div>
                      <Field className="mb-0 sm:w-1/2">
                        <Label>Meeting Time</Label>
                        <Input type="time" value={form.meetingTime} onChange={v=>upd("meetingTime",v)} placeholder=""/>
                      </Field>
                    </div>

                    {form.originLat && form.destinationLat && (
                      <RouteMap 
                        origin={form.origin} 
                        destination={form.destination}
                        originCoords={[form.originLat, form.originLng]}
                        destCoords={[form.destinationLat, form.destinationLng]}
                        meetingName={form.meetingPoint}
                        meetingCoords={form.meetingLat ? [form.meetingLat, form.meetingLng] : null}
                        distanceMiles={miles}
                      />
                    )}
                  </SectionCard>

                  {/* 2. Dimensions */}
                  <SectionCard icon="📐" title="Load Dimensions & Specs" eyebrow="Specifications">

                    {/* Permit Intelligence Upload */}
                    <div className="mb-4 rounded-xl p-3 sm:p-4" style={{ background:T.tealLight, border:`1px solid ${T.teal}30` }}>
                      <div className="flex items-center gap-2 mb-2">
                        <span style={{ fontSize:15 }}>🧠</span>
                        <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color:T.tealDark }}>Permit Intelligence — Auto-Fill from Permit</div>
                      </div>
                      <p className="text-[11px] mb-2" style={{ color:T.slate }}>
                        Paste the text of your oversize permit and AI will pull out dimensions, route, curfews, and escort requirements to fill this form for you.
                      </p>
                      <textarea
                        value={permitText}
                        onChange={e=>setPermitText(e.target.value)}
                        placeholder="Paste permit text here…"
                        rows={3}
                        style={{ width:"100%", background:T.white, border:`1.5px solid ${T.line}`, borderRadius:8, padding:"10px", color:T.text, fontSize:12.5, outline:"none", fontFamily:"inherit", resize:"vertical" }}
                      />
                      <div className="flex items-center justify-between mt-2 gap-2">
                        <span className="text-[10px]" style={{ color:T.muted }}>{permitError || "You review every field before it's saved."}</span>
                        <button
                          type="button"
                          onClick={handlePermitExtract}
                          disabled={permitLoading || !permitText.trim()}
                          className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold disabled:opacity-50"
                          style={{ background:T.teal, color:T.onAccent }}
                        >
                          {permitLoading ? "Extracting…" : "Extract & Auto-Fill"}
                        </button>
                      </div>
                    </div>

                    <Field>
                      <Label>Commodity / Load Type</Label>
                      <Select value={form.loadType} onChange={v=>upd("loadType",v)} options={LOAD_TYPES} placeholder="Select load type"/>
                    </Field>
                    {form.loadType==="Custom/Other" && (
                      <Field>
                        <Label>Custom Description</Label>
                        <Input value={form.commodity} onChange={v=>upd("commodity",v)} placeholder="Describe your load"/>
                      </Field>
                    )}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                      <div>
                        <Label>Width</Label>
                        <Select value={form.width} onChange={v=>upd("width",v)} options={WIDTH_OPTIONS} placeholder="Select..."/>
                      </div>
                      <div>
                        <Label>Height</Label>
                        <Select value={form.height} onChange={v=>upd("height",v)} options={HEIGHT_OPTIONS} placeholder="Select..."/>
                      </div>
                      <div>
                        <Label>Length</Label>
                        <Input value={form.length} onChange={v=>upd("length",v)} placeholder="110'" suffix="ft"/>
                      </div>
                      <div>
                        <Label>Weight</Label>
                        <Input value={form.weight} onChange={v=>upd("weight",v)} placeholder="80,000" suffix="lbs"/>
                      </div>
                    </div>

                    {/* Auto-Escort Requirement Engine — live warnings */}
                    {escortFlags.warnings.length > 0 && (
                      <div className="mt-4 rounded-xl p-3 sm:p-4" style={{ background:T.amberLight, border:`1px solid ${T.amber}30` }}>
                        <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color:T.amber }}>⚙️ Smart Escort Engine — Live Assessment</div>
                        <div className="space-y-1.5">
                          {escortFlags.warnings.map((w,i) => (
                            <div key={i} className="flex items-start gap-2 text-[11.5px]" style={{ color:T.text }}>
                              <span className="flex-shrink-0">{w.level==="high"?"🔴":w.level==="medium"?"🟠":"🟡"}</span>
                              <span>{w.text}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] mt-2" style={{ color:T.muted }}>Advisory only — based on common thresholds, not your exact state's rules. Verify against the actual DOT/CHP permit.</p>
                      </div>
                    )}

                    <div className="mt-4 sm:mt-5 rounded-xl p-3 sm:p-4" style={{ background:T.line2, border:`1px solid ${T.line}` }}>
                      <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color:T.amber }}>📌 Dimension Verification</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Checkbox
                          label="Escort Confirms Dimensions"
                          sub="Escort has verified width, height, length & weight above"
                          checked={form.escortConfirmed}
                          onChange={v=>upd("escortConfirmed",v)}
                        />
                        <Checkbox
                          label="Trucker Confirms Dimensions"
                          sub="Trucker has verified width, height, length & weight above"
                          checked={form.truckerConfirmed}
                          onChange={v=>upd("truckerConfirmed",v)}
                        />
                      </div>
                    </div>
                  </SectionCard>

                  {/* 3. Escorts */}
                  <SectionCard icon="🛡️" title="Required Escorts" eyebrow="Safety Requirements">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Checkbox label="Front Escort" sub="Lead vehicle ahead of load" checked={form.frontEscort} onChange={v=>upd("frontEscort",v)}/>
                      <Checkbox label="Chase Escort" sub="Follow vehicle behind load" checked={form.chaseEscort} onChange={v=>upd("chaseEscort",v)}/>
                      <Checkbox label="Height Pole" sub="Overhead clearance checks" checked={form.heightPole} onChange={v=>upd("heightPole",v)}/>
                      <Checkbox label="Steersman" sub="Rear steering operator" checked={form.steersman} onChange={v=>upd("steersman",v)}/>
                    </div>
                  </SectionCard>

                  {/* 4. Pay */}
                  <SectionCard icon="💰" title="Driver Pay & Rate Info" eyebrow="Compensation">
                    <RateCalculatorSection
                      form={form} upd={upd} catalog={catalog}
                      miles={miles} milesSource={milesSource} milesLoading={milesLoading}
                      est={estimate} esc={effEsc}
                      SelectEl={<Select value={form.fuelSurcharge} onChange={v=>upd("fuelSurcharge",v)} options={["Included","Not Included","TBD"]} placeholder="Select..."/>}
                    />

                    <div className="rounded-xl p-3 sm:p-4 mt-4" style={{ background:T.line2, border:`1px solid ${T.line}` }}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-5 h-5 rounded-md flex items-center justify-center text-[11px]" style={{ background:`${T.amber}18`, color:T.amber }}>⚙</span>
                        <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color:T.amber }}>Payment Terms</div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field className="mb-0"><Label>Method</Label><Select value={form.paymentMethod} onChange={v=>upd("paymentMethod",v)} options={["Zelle","ACH Bank Transfer","Check","Wire Transfer","Other"]} placeholder="Method"/></Field>
                        <Field className="mb-0"><Label>Terms</Label><Select value={form.paymentTerms} onChange={v=>upd("paymentTerms",v)} options={["Same Day","NET 7","NET 15","NET 30","Upon Completion"]} placeholder="Terms"/></Field>
                      </div>
                    </div>
                  </SectionCard>

                  {/* 5. Dispatch Info */}
                  <SectionCard icon="📞" title="Dispatch Contact Info" eyebrow="Contact">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field className="mb-0"><Label>Company Name</Label><Input value={form.companyName} onChange={v=>upd("companyName",v)} placeholder="e.g., GreenBridge Energy"/></Field>
                      <Field className="mb-0"><Label>Dispatcher Name</Label><Input value={form.dispatcherName} onChange={v=>upd("dispatcherName",v)} placeholder="e.g., James Miller"/></Field>
                      <Field className="mb-0"><Label>Dispatch Phone</Label><Input type="tel" value={form.dispatchPhone} onChange={v=>upd("dispatchPhone",v)} placeholder="(512) 555-0198"/></Field>
                      <Field className="mb-0"><Label>Dispatch Email</Label><Input type="email" value={form.dispatchEmail} onChange={v=>upd("dispatchEmail",v)} placeholder="dispatch@company.com"/></Field>
                    </div>
                    <div className="mt-3 pt-3" style={{ borderTop:`1px solid ${T.line2}` }}>
                      <Field className="mb-0">
                        <Label>📋 Company Records Email (optional)</Label>
                        <Input type="email" value={form.recordsEmail} onChange={v=>upd("recordsEmail",v)} placeholder="records@company.com"/>
                        <p className="text-[11px] mt-1.5" style={{ color:T.muted }}>Every event on this load — posted, offer sent, driver accepted — gets a copy emailed here too, for your own audit trail.</p>
                      </Field>
                    </div>
                  </SectionCard>

                  {/* 6. Notes */}
                  <SectionCard icon="📝" title="Additional Notes" eyebrow="Notes">
                    <Label>Special Instructions or Requirements</Label>
                    <textarea value={form.notes} onChange={e=>upd("notes",e.target.value)}
                      placeholder="e.g., Requires night travel permit, bridge weight limit restrictions, contact driver 2hrs before pickup..." rows={4}
                      style={{ width:"100%", background:T.white, border:`1.5px solid ${T.line}`, borderRadius:10, padding:"12px", color:T.text, fontSize:14, outline:"none", fontFamily:"inherit", resize:"vertical", lineHeight:1.6 }}
                      onFocus={e=>e.target.style.borderColor=T.teal} onBlur={e=>e.target.style.borderColor=T.line}/>
                  </SectionCard>
                </div>

                {/* ── RIGHT SIDEBAR ── */}
                <div className="w-full lg:w-[320px] xl:w-[360px] flex-shrink-0 lg:sticky lg:top-6 space-y-4 sm:space-y-5 pb-6 lg:pb-0 z-10">
                  <LoadPreview form={form} featured={featured} payDisplay={estimate.pay !== "Contact dispatcher" ? estimate.pay : ""}/>

                  {/* Featured listing toggle */}
                  <div className="rounded-2xl p-4" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
                    <div onClick={()=>setFeatured(p=>!p)} className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-3">
                        <span className="text-[22px]">⚡</span>
                        <div>
                          <div className="font-bold text-[14px]" style={{ color:T.text }}>Featured Listing</div>
                          <div className="text-[12px]" style={{ color:T.muted }}>Get matched up to 3x faster</div>
                        </div>
                      </div>
                      <div className="w-6 h-6 rounded flex-shrink-0 flex items-center justify-center transition-colors" style={{ border:`2px solid ${featured?T.amber:T.muted}`, background:featured?T.amber:"transparent" }}>
                        {featured && <span className="text-white text-sm font-bold">✓</span>}
                      </div>
                    </div>
                    {featured && (
                      <div className="mt-3 p-3 rounded-lg text-[12px] font-semibold leading-relaxed" style={{ background:T.amberLight, color:T.amber }}>
                        Your load appears at the top of search results and gets priority notifications.
                      </div>
                    )}
                  </div>

                  {/* Checkout / API Trigger */}
                  <div className="rounded-2xl p-5 sm:p-6" style={{ background:T.surface, border:`1px solid ${T.line}`, boxShadow:T.shadow }}>
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-lg" style={{ color:T.teal }}>$</span>
                      <span className="font-bold text-[16px]" style={{ color:T.text }}>Order Summary</span>
                    </div>
                    {[
                      { label:"Standard Lead Fee", value:"$15.00" },
                      { label:"Email Notifications", value:"Included", valueColor:T.green },
                      ...(featured ? [{ label:"Featured Listing", value:"+$20.00", valueColor:T.amber }] : []),
                    ].map(r=>(
                      <div key={r.label} className="flex justify-between py-2.5 border-b" style={{ borderColor: T.line2 }}>
                        <span className="text-[13px] sm:text-sm" style={{ color:T.slate }}>{r.label}</span>
                        <span className="text-[13px] sm:text-sm font-bold" style={{ color:r.valueColor||T.text }}>{r.value}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-4">
                      <span className="font-bold text-[15px]" style={{ color:T.text }}>Total Due Today</span>
                      <span className="font-bold text-[22px]" style={{ color:T.teal }}>${total}.00</span>
                    </div>
                    <div className="text-right text-[11px] mb-4 sm:mb-5" style={{ color:T.muted }}>Charged securely via Stripe</div>

                    <button 
                      onClick={() => setReviewOpen(true)}
                      disabled={!canSubmit || isSubmitting}
                      className="w-full py-3.5 sm:py-4 rounded-xl border-none font-bold text-[15px] transition-colors"
                      style={{ background:canSubmit?T.teal:T.mutedBg, color:canSubmit?T.onAccent:T.muted, cursor:canSubmit?"pointer":"not-allowed" }}
                    >
                      {isSubmitting ? "Processing..." : "Review & Post →"}
                    </button>

                    {!canSubmit && (
                      <div className="mt-4 text-center text-[12px]" style={{ color:T.muted }}>Fill in Origin, Destination & Date to continue</div>
                    )}
                  </div>
                </div>
                
              </div>
            </div>
          )}
        </main>
        
        <BottomNav activeNav={activeNav} setActiveNav={setActiveNav} />

        {reviewOpen && (
          <ReviewPostModal
            form={form} featured={featured} est={estimate} miles={miles} milesSource={milesSource}
            esc={effEsc} escortFlags={escortFlags} catalog={catalog} fee={total}
            mapEl={form.originLat && form.destinationLat ? (
              <RouteMap
                origin={form.origin} destination={form.destination}
                originCoords={[form.originLat, form.originLng]} destCoords={[form.destinationLat, form.destinationLng]}
                meetingName={form.meetingPoint} meetingCoords={form.meetingLat ? [form.meetingLat, form.meetingLng] : null}
                distanceMiles={miles}
              />
            ) : null}
            submitting={isSubmitting} onBack={() => setReviewOpen(false)} onConfirm={handlePostLoad}
          />
        )}
      </div>
    </div>
  );
}